import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiService } from '../../services/apiService';

/**
 * Shared building blocks for the /admin group panels. Every panel talks to the
 * backend through the two generic dispatcher calls (adminPanelGet/Post), so
 * this file also owns the tiny data-fetch hook they all use.
 */

const uid = () => apiService.getStoredUserId() || '';

export async function panelGet(section: string, action: string,
                               params: Record<string, string | number> = {}) {
  const r = await apiService.adminPanelGet(uid(), section, action, params);
  if (r.success && r.data?.success) return { data: r.data.data, error: '' };
  return { data: null, error: r.data?.detail || r.error || 'Request failed' };
}

export async function panelPost(section: string, action: string,
                                body: Record<string, any> = {}) {
  const r = await apiService.adminPanelPost(uid(), section, action, body);
  if (r.success && r.data?.success) return { data: r.data.data, error: '' };
  return { data: null, error: r.data?.detail || r.error || 'Request failed' };
}

/** Fetch-on-mount (and on dep change) with loading/error/reload. */
export function useAdminGet<T = any>(section: string, action: string,
                                     params: Record<string, string | number> = {},
                                     deps: any[] = []) {
  const [state, setState] = useState<{ data: T | null; loading: boolean; error: string }>(
    { data: null, loading: true, error: '' });
  // Last request wins. Two fetches can be in flight when a change to one dep
  // also resets another (a new search resetting the page to 0): the first
  // fires with the stale page, the second with the right one, and whichever
  // answered last used to be the one displayed. Now only the newest counts.
  const seq = useRef(0);
  const load = useCallback(async () => {
    const mine = ++seq.current;
    setState(s => ({ ...s, loading: true, error: '' }));
    const { data, error } = await panelGet(section, action, params);
    if (mine !== seq.current) return;
    setState({ data, loading: false, error });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => { load(); }, [load]);
  return { ...state, reload: load };
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------
export const fmtDate = (v: any): string => {
  if (v === null || v === undefined || v === '') return '—';
  const d = new Date(v);
  if (isNaN(d.getTime())) return String(v);
  return d.toLocaleString('en-GB', { year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit' });
};

const looksLikeDate = (k: string) =>
  /(date|_at|when|created|updated|submitted|requested|signed up|last |sent_at)/i.test(k);

export const fmtCell = (key: string, v: any): string => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? '✓' : '—';
  if (looksLikeDate(key) && (typeof v === 'string' || v instanceof Date)) return fmtDate(v);
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

const labelize = (k: string) =>
  k.replace(/^_/, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

// ---------------------------------------------------------------------------
// Atoms
// ---------------------------------------------------------------------------
/** A headline number. Give it `onClick` and it becomes the control for the
 *  thing it counts — click "Signed in" to see the signed-in visits — with
 *  `active` showing which one is currently narrowing the page. Cards without
 *  an onClick render exactly as before. */
export const KPI: React.FC<{
  label: string; value: React.ReactNode; hint?: string;
  onClick?: () => void; active?: boolean;
}> = ({ label, value, hint, onClick, active }) => {
  const look = active
    ? 'border-primary-500 ring-1 ring-primary-500 bg-primary-50'
    : 'border-grayscale-300 bg-white';
  const inner = (
    <>
      <div className="text-[11px] uppercase tracking-wide text-grayscale-1100">{label}</div>
      <div className="text-xl font-bold text-grayscale-1300 font-grotesk">{value}</div>
      {hint && <div className="text-[11px] text-grayscale-1100">{hint}</div>}
    </>
  );
  if (!onClick) {
    return (
      <div className={`rounded-xl border px-4 py-3 min-w-[120px] flex-1 ${look}`}>{inner}</div>
    );
  }
  return (
    <button type="button" onClick={onClick}
      title={active ? 'Filtering by this — click to clear' : 'Click to filter by this'}
      className={`rounded-xl border px-4 py-3 min-w-[120px] flex-1 text-left
        cursor-pointer hover:border-primary-400 hover:bg-primary-50 ${look}`}>
      {inner}
    </button>
  );
};

export const KPIRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex gap-3 flex-wrap">{children}</div>
);

export const Section: React.FC<{ title: string; right?: React.ReactNode; children: React.ReactNode }> =
  ({ title, right, children }) => (
  <div className="bg-white rounded-xl border border-grayscale-300 p-4 flex flex-col gap-3">
    <div className="flex items-center justify-between flex-wrap gap-2">
      <h3 className="text-sm font-bold text-grayscale-1300 font-grotesk">{title}</h3>
      {right}
    </div>
    {children}
  </div>
);

export const Btn: React.FC<{
  onClick?: () => void; children: React.ReactNode; kind?: 'primary' | 'subtle' | 'danger';
  disabled?: boolean; small?: boolean; title?: string;
}> = ({ onClick, children, kind = 'subtle', disabled, small, title }) => {
  const base = small ? 'py-1 px-2.5 text-xs' : 'py-1.5 px-3.5 text-sm';
  const look = kind === 'primary'
    ? 'bg-primary-500 text-grayscale-100 disabled:bg-primary-100 disabled:text-primary-300'
    : kind === 'danger'
      ? 'bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 disabled:opacity-50'
      : 'border border-grayscale-400 text-grayscale-1200 hover:bg-grayscale-200 disabled:opacity-50';
  return (
    <button onClick={onClick} disabled={disabled} title={title}
      className={`${base} rounded-full font-grotesk font-medium whitespace-nowrap ${look}`}>
      {children}
    </button>
  );
};

export const TextInput: React.FC<{
  value: string; onChange: (v: string) => void; placeholder?: string; type?: string; wide?: boolean;
}> = ({ value, onChange, placeholder, type = 'text', wide }) => (
  <input type={type} value={value} placeholder={placeholder}
    onChange={e => onChange(e.target.value)}
    className={`h-9 px-3 rounded-md border-[0.5px] border-primary-300 bg-white text-sm ${wide ? 'flex-1 min-w-[200px]' : 'w-48'}`} />
);

export const Select: React.FC<{
  value: string; onChange: (v: string) => void; options: string[]; labels?: Record<string, string>;
}> = ({ value, onChange, options, labels }) => (
  <select value={value} onChange={e => onChange(e.target.value)}
    className="h-9 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm">
    {options.map(o => <option key={o} value={o}>{labels?.[o] ?? o}</option>)}
  </select>
);

/** One active filter, with its own remove button. Shown together they are the
 *  answer to "why am I looking at 6 rows" — which a filter UI has to make
 *  visible or people conclude the data is wrong. */
export const Chip: React.FC<{ label: string; value: string; onClear: () => void }> =
  ({ label, value, onClear }) => (
  <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-50 border
    border-primary-300 pl-2.5 pr-1 py-0.5 text-xs text-grayscale-1300 whitespace-nowrap">
    <span className="text-grayscale-1100">{label}</span>
    <span className="font-medium max-w-[14rem] truncate" title={value}>{value}</span>
    <button type="button" onClick={onClear} title="Remove this filter"
      className="w-4 h-4 rounded-full hover:bg-primary-200 text-grayscale-1100 leading-none">×</button>
  </span>
);

export const Msg: React.FC<{ kind: 'error' | 'ok'; children: React.ReactNode }> = ({ kind, children }) => (
  <div className={`text-sm rounded-lg px-3 py-2 border ${kind === 'error'
    ? 'text-red-600 bg-red-50 border-red-200'
    : 'text-green-700 bg-green-50 border-green-200'}`}>{children}</div>
);

export const Loading: React.FC<{ label?: string }> = ({ label }) => (
  <div className="text-sm text-grayscale-1100 py-6 text-center">{label || 'Loading…'}</div>
);

export const SubTabs: React.FC<{ tabs: string[]; active: string; onChange: (t: string) => void }> =
  ({ tabs, active, onChange }) => (
  <div className="flex gap-1 flex-wrap">
    {tabs.map(t => (
      <button key={t} onClick={() => onChange(t)}
        className={`py-1.5 px-3 rounded-full text-xs font-grotesk whitespace-nowrap ${active === t
          ? 'bg-primary-500 text-grayscale-100 font-bold'
          : 'border border-grayscale-400 text-grayscale-1200 hover:bg-grayscale-200'}`}>
        {t}
      </button>
    ))}
  </div>
);

// ---------------------------------------------------------------------------
// DataTable — generic table over row objects with search + CSV export.
// ---------------------------------------------------------------------------
const readColPref = (key: string): { hidden: string[]; shown: string[] } => {
  try {
    const v = JSON.parse(localStorage.getItem(key) || '{}');
    return { hidden: Array.isArray(v.hidden) ? v.hidden.map(String) : [], shown: Array.isArray(v.shown) ? v.shown.map(String) : [] };
  } catch { return { hidden: [], shown: [] }; }
};
export const DataTable: React.FC<{
  rows: Record<string, any>[];
  /** Column order + which columns show. Defaults to the first row's keys minus _-prefixed. */
  columns?: string[];
  labels?: Record<string, string>;
  searchable?: boolean;
  csvName?: string;
  maxHeight?: string;
  empty?: string;
  /** Renders trailing action buttons per row. */
  actions?: (row: Record<string, any>) => React.ReactNode;
  render?: Record<string, (row: Record<string, any>) => React.ReactNode>;
  /** Column to sort by before anyone clicks a header, e.g. '-searches' for
   *  descending. Rows arrive in the server's order otherwise. */
  defaultSort?: string;
  /** For a server-paged table: the sort in force and where to send a change.
   *  Local sorting is skipped, because reordering one page of a paged set
   *  would put the "top" of that page above rows on other pages that beat it. */
  serverSort?: { key: string; dir: 'asc' | 'desc' } | null;
  onSortChange?: (key: string, dir: 'asc' | 'desc' | '') => void;
  /** Columns whose header does not sort — for a server-paged table, the ones
   *  the server has no sort key for. */
  unsortable?: string[];
  /** More columns the rows carry, hidden until someone turns them on with
   *  the Columns picker. */
  optionalColumns?: string[];
  /** Where this table's column choice is remembered (per browser). Defaults
   *  to csvName, else the default column list. */
  tableId?: string;
}> = ({ rows, columns, labels, searchable, csvName, maxHeight = '28rem', empty, actions, render,
        defaultSort, serverSort, onSortChange, unsortable, optionalColumns, tableId }) => {
  const [q, setQ] = useState('');
  // Sorting and filtering live here, in the one table every panel uses, so
  // all 130-odd tables get them at once rather than one panel at a time.
  const [sortKey, setSortKey] = useState<string>(defaultSort?.replace(/^-/, '') || '');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(defaultSort?.startsWith('-') ? 'desc' : 'asc');
  const [colFilters, setColFilters] = useState<Record<string, string>>({});
  const [showColFilters, setShowColFilters] = useState(false);
  const baseCols = useMemo(() =>
    columns || (rows[0] ? Object.keys(rows[0]).filter(k => !k.startsWith('_')) : []),
    [columns, rows]);
  // Columns picker (owner, 2026-10-05: "give option to customise columns
  // visible"). Remembered as what was HIDDEN of the defaults and what was
  // ADDED of the optional ones — not as a fixed list — so a column a panel
  // adds later still shows up for someone who customised before.
  const allCols = useMemo(() => [...baseCols, ...(optionalColumns || []).filter(c => !baseCols.includes(c))],
    [baseCols, optionalColumns]);
  const colKey = `utech_admin_cols:${tableId || csvName || baseCols.slice(0, 6).join(',')}`;
  const [colPref, setColPref] = useState<{ hidden: string[]; shown: string[] }>(() => readColPref(colKey));
  useEffect(() => { setColPref(readColPref(colKey)); }, [colKey]);
  const [showPicker, setShowPicker] = useState(false);
  const cols = useMemo(() => {
    const out = allCols.filter(c => baseCols.includes(c) ? !colPref.hidden.includes(c) : colPref.shown.includes(c));
    return out.length ? out : baseCols.slice(0, 1);
  }, [allCols, baseCols, colPref]);
  const setCol = (c: string, on: boolean) => {
    const isBase = baseCols.includes(c);
    const next = {
      hidden: isBase ? (on ? colPref.hidden.filter(x => x !== c) : [...colPref.hidden, c]) : colPref.hidden,
      shown: !isBase ? (on ? [...colPref.shown, c] : colPref.shown.filter(x => x !== c)) : colPref.shown,
    };
    setColPref(next);
    try { localStorage.setItem(colKey, JSON.stringify(next)); } catch { /* private mode */ }
  };
  const resetCols = (all = false) => {
    const next = all ? { hidden: [], shown: allCols.filter(c => !baseCols.includes(c)) } : { hidden: [], shown: [] };
    setColPref(next);
    try { localStorage.setItem(colKey, JSON.stringify(next)); } catch { /* private mode */ }
  };

  const server = !!onSortChange;
  const effKey = server ? (serverSort?.key || '') : sortKey;
  const effDir = server ? (serverSort?.dir || 'asc') : sortDir;
  const clickSort = (c: string) => {
    // asc -> desc -> off, so a third click gives the server's order back.
    const next: [string, 'asc' | 'desc' | ''] =
      effKey !== c ? [c, 'asc'] : effDir === 'asc' ? [c, 'desc'] : ['', ''];
    if (server) { onSortChange!(next[0], next[1]); return; }
    setSortKey(next[0]); if (next[1]) setSortDir(next[1]);
  };

  const view = useMemo(() => {
    let out = rows;
    const needle = q.trim().toLowerCase();
    if (needle) {
      out = out.filter(r => cols.some(c => String(r[c] ?? '').toLowerCase().includes(needle)));
    }
    const active = Object.entries(colFilters).filter(([, v]) => v.trim());
    if (active.length) {
      out = out.filter(r => active.every(([c, v]) =>
        String(fmtCell(c, r[c])).toLowerCase().includes(v.trim().toLowerCase())));
    }
    if (sortKey && !server) {
      // Compare by what the value IS, not what it looks like: numbers as
      // numbers, dates as dates, everything else as case-folded text. Empty
      // cells sink to the bottom whichever way the sort runs — including an
      // empty array (list columns such as the curated TTO emails hand back []
      // for "none"), which would otherwise stringify to '' and float to the
      // top as the smallest text. A populated array sorts by its first entry.
      const rank = (v: any): [number, number | string] => {
        if (v === null || v === undefined || v === '') return [1, ''];
        if (Array.isArray(v)) {
          return v.length === 0 ? [1, ''] : [0, v.map(x => String(x ?? '')).join(', ').toLowerCase()];
        }
        if (typeof v === 'number') return [0, v];
        if (typeof v === 'boolean') return [0, v ? 1 : 0];
        if (typeof v === 'string') {
          const t = v.trim();
          if (t !== '' && !Number.isNaN(Number(t))) return [0, Number(t)];
          if (looksLikeDate(sortKey)) {
            const ms = Date.parse(t);
            if (!Number.isNaN(ms)) return [0, ms];
          }
          return [0, t.toLowerCase()];
        }
        return [0, String(v).toLowerCase()];
      };
      const dir = sortDir === 'asc' ? 1 : -1;
      out = [...out].sort((a, b) => {
        const [ea, va] = rank(a[sortKey]); const [eb, vb] = rank(b[sortKey]);
        if (ea !== eb) return ea - eb;                       // empties last, always
        if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
        return String(va).localeCompare(String(vb), undefined, { numeric: true }) * dir;
      });
    }
    return out;
  }, [rows, q, cols, colFilters, sortKey, sortDir]);

  const downloadCsv = () => {
    const esc = (s: any) => `"${String(s ?? '').replace(/"/g, '""')}"`;
    const csv = [cols.map(esc).join(',')]
      .concat(view.map(r => cols.map(c => esc(fmtCell(c, r[c]))).join(',')))
      .join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `${csvName || 'export'}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (!rows.length) {
    return <div className="text-sm text-grayscale-1100 py-4 text-center">{empty || 'Nothing here yet.'}</div>;
  }
  return (
    <div className="flex flex-col gap-2">
      {(searchable || csvName || rows.length > 5) && (
        <div className="flex items-center gap-2 flex-wrap">
          <TextInput value={q} onChange={setQ} placeholder="Filter rows…" wide />
          <Btn small onClick={() => setShowColFilters(v => !v)}
            title="Filter one column at a time">
            {showColFilters ? 'Hide column filters' : 'Filter by column'}
          </Btn>
          <span className="text-xs text-grayscale-1100">
            {view.length === rows.length ? `${rows.length} row${rows.length === 1 ? '' : 's'}`
                                         : `${view.length} of ${rows.length}`}
            {effKey && <> · sorted by {labels?.[effKey] ?? labelize(effKey)} {effDir === 'asc' ? '↑' : '↓'}</>}
          </span>
          {(q || Object.values(colFilters).some(v => v.trim()) || effKey) && (
            <button type="button"
              onClick={() => { setQ(''); setColFilters({}); if (server) onSortChange!('', ''); else setSortKey(''); }}
              className="text-xs text-grayscale-1100 underline underline-offset-2">reset</button>
          )}
          {allCols.length > 1 && (
            <span className="relative">
              <Btn small onClick={() => setShowPicker(v => !v)}
                title="Choose which columns show (remembered in this browser)">
                Columns {cols.length}/{allCols.length}
              </Btn>
              {showPicker && (
                <div className="absolute z-30 mt-1 right-0 w-72 max-h-[22rem] overflow-auto rounded-lg border border-grayscale-200 bg-white shadow-lg p-2 flex flex-col gap-1">
                  <div className="flex items-center gap-2 text-[11px] text-grayscale-1100 pb-1 border-b border-grayscale-200">
                    <span className="flex-1">Shown in this table · CSV downloads these</span>
                    <button type="button" className="underline underline-offset-2" onClick={() => resetCols(true)}>all</button>
                    <button type="button" className="underline underline-offset-2" onClick={() => resetCols(false)}>default</button>
                    <button type="button" aria-label="Close" className="px-1" onClick={() => setShowPicker(false)}>✕</button>
                  </div>
                  {allCols.map(c => {
                    const on = cols.includes(c);
                    return (
                      <label key={c} className="flex items-center gap-2 text-xs text-grayscale-1300 px-1 py-0.5 rounded hover:bg-grayscale-100 cursor-pointer">
                        <input type="checkbox" checked={on} onChange={e => setCol(c, e.target.checked)} />
                        <span className="flex-1">{labels?.[c] ?? labelize(c)}</span>
                        {!baseCols.includes(c) && <span className="text-[10px] text-grayscale-1100">extra</span>}
                      </label>
                    );
                  })}
                </div>
              )}
            </span>
          )}
          {csvName && <Btn small onClick={downloadCsv}>Download CSV</Btn>}
        </div>
      )}
      <div className="overflow-auto rounded-lg border border-grayscale-300" style={{ maxHeight }}>
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-grayscale-200">
            <tr>
              {cols.map(c => unsortable?.includes(c) ? (
                <th key={c} className="text-left font-grotesk text-xs px-3 py-2 whitespace-nowrap text-grayscale-1200">
                  {labels?.[c] ?? labelize(c)}
                </th>
              ) : (
                <th key={c}
                    onClick={() => clickSort(c)}
                    title={effKey === c
                      ? (effDir === 'asc' ? 'Sorted ascending — click for descending' : 'Sorted descending — click to clear')
                      : 'Click to sort'}
                    className={`text-left font-grotesk text-xs px-3 py-2 whitespace-nowrap cursor-pointer select-none
                      hover:text-grayscale-1300 ${effKey === c ? 'text-grayscale-1300 font-bold' : 'text-grayscale-1200'}`}>
                  {labels?.[c] ?? labelize(c)}
                  <span aria-hidden className="ml-1 text-[10px] opacity-70">
                    {effKey === c ? (effDir === 'asc' ? '▲' : '▼') : ''}
                  </span>
                </th>
              ))}
              {actions && <th className="px-3 py-2" />}
            </tr>
            {showColFilters && (
              <tr className="bg-grayscale-100">
                {cols.map(c => (
                  <th key={c} className="px-2 py-1">
                    <input value={colFilters[c] || ''} placeholder="filter…"
                      onChange={e => setColFilters(f => ({ ...f, [c]: e.target.value }))}
                      onClick={e => e.stopPropagation()}
                      className="w-full min-w-[6rem] h-7 px-2 rounded border-[0.5px] border-primary-300 bg-white text-xs font-normal" />
                  </th>
                ))}
                {actions && <th />}
              </tr>
            )}
          </thead>
          <tbody>
            {view.map((r, i) => (
              <tr key={i} className="border-t border-grayscale-200 hover:bg-grayscale-100">
                {cols.map(c => (
                  <td key={c} className="px-3 py-1.5 text-grayscale-1300 whitespace-nowrap max-w-[26rem] overflow-hidden text-ellipsis"
                      title={typeof r[c] === 'string' ? r[c] : undefined}>
                    {render?.[c] ? render[c](r) : fmtCell(c, r[c])}
                  </td>
                ))}
                {actions && <td className="px-3 py-1.5 whitespace-nowrap">{actions(r)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// BarList — dependency-free horizontal bar breakdown (replaces the panels'
// plotly pies: same numbers, honest proportions, no chart library).
// ---------------------------------------------------------------------------
export const BarList: React.FC<{
  items: { label: string; value: number }[]; total?: number;
  /** Makes each row a filter control for the value it represents. */
  onSelect?: (label: string) => void; activeLabel?: string;
}> = ({ items, total, onSelect, activeLabel }) => {
  const max = Math.max(1, ...items.map(i => i.value));
  const sum = total ?? items.reduce((a, b) => a + b.value, 0);
  return (
    <div className="flex flex-col gap-1.5">
      {items.map(i => (
        <div key={i.label}
          onClick={onSelect ? () => onSelect(i.label) : undefined}
          title={onSelect ? (activeLabel === i.label
            ? 'Filtering by this — click to clear' : `Filter by ${i.label}`) : i.label}
          className={`flex items-center gap-2 text-sm rounded-md px-1 -mx-1 ${
            onSelect ? 'cursor-pointer hover:bg-primary-50' : ''} ${
            activeLabel === i.label ? 'bg-primary-50 ring-1 ring-primary-300' : ''}`}>
          <div className="w-40 truncate text-grayscale-1200" title={i.label}>{i.label}</div>
          <div className="flex-1 bg-grayscale-200 rounded-full h-3 overflow-hidden">
            <div className="h-3 bg-primary-500 rounded-full"
                 style={{ width: `${(i.value / max) * 100}%` }} />
          </div>
          <div className="w-20 text-right text-grayscale-1300 font-medium">
            {i.value.toLocaleString()}
            <span className="text-grayscale-1100 text-xs"> ({sum ? Math.round((i.value / sum) * 100) : 0}%)</span>
          </div>
        </div>
      ))}
    </div>
  );
};

/** Two-step destructive confirm: first click arms, second within 5s fires. */
export const DangerBtn: React.FC<{ onConfirm: () => void; children: React.ReactNode; small?: boolean }> =
  ({ onConfirm, children, small }) => {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), 5000);
    return () => clearTimeout(t);
  }, [armed]);
  return (
    <Btn kind="danger" small={small}
      onClick={() => { if (armed) { setArmed(false); onConfirm(); } else setArmed(true); }}>
      {armed ? 'Click again to confirm' : children}
    </Btn>
  );
};
